import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Autoplay } from 'swiper/modules';
import { FaFire, FaBolt, FaCopy, FaHeart, FaRegHeart } from 'react-icons/fa';
import '../assets/styles/Sales.scss';
import shopingCardData from './data/shopingCardData.json';
import { useWishlist } from '../context/WishlistContext.jsx';

const SalesPage = () => {
    const navigate = useNavigate();
    const { toggleWishlist, isInWishlist } = useWishlist();
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });

    useEffect(() => {
        AOS.init({ 
            duration: 1500, 
            once: true,
            easing: 'ease-out-quad'
        });

        const targetDate = new Date().getTime() + (48 * 60 * 60 * 1000);

        const timer = setInterval(() => {
            const now = new Date().getTime();
            const difference = targetDate - now;

            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((difference % (1000 * 60)) / 1000);

            if (difference < 0) {
                clearInterval(timer);
            } else {
                setTimeLeft({ days, hours, minutes, seconds });
            }
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Coupon code copied to clipboard!');
    };

    const saleProducts = shopingCardData.shopingCard;

    return (
        <div className="sales-page-container padding_t88">
            <section className="sales-hero">
                <picture>
                    <source media="(max-width: 575px)" srcSet="/images/sales-hero-mobile.png" />
                    <img src="/images/sales-hero.png" alt="Sales Banner" className="hero-bg" />
                </picture>
                <div className="hero-content">
                    <Container>
                        <Col lg={7}>
                            <div className="sale-badge" data-aos="fade-down">Limited Time Offer</div>
                            <h1 className="hero-title" data-aos="fade-right" data-aos-delay="200">
                                SEASONAL<br />CLEARANCE
                            </h1>
                            <p className="lead text-white opacity-75 mb-4" data-aos="fade-up" data-aos-delay="400">
                                Dive into our exclusive collection with up to 70% off.
                            </p>

                            <div className="countdown-timer d-flex justify-content-center" data-aos="zoom-in" data-aos-delay="600">
                                <div className="timer-block">
                                    <span className="time">{timeLeft.days}</span>
                                    <span className="label">Days</span>
                                </div>
                                <div className="timer-block">
                                    <span className="time">{timeLeft.hours}</span>
                                    <span className="label">Hours</span>
                                </div>
                                <div className="timer-block">
                                    <span className="time">{timeLeft.minutes}</span>
                                    <span className="label">Mins</span>
                                </div>
                                <div className="timer-block">
                                    <span className="time">{timeLeft.seconds}</span>
                                    <span className="label">Secs</span>
                                </div>
                            </div>
                        </Col>
                    </Container>
                </div>
            </section>

            <section className="flash-deals-section">
                <Container>
                    <div className="section-header text-center" data-aos="fade-up">
                        <span className="section-subtitle"><FaBolt className="hot-icon text-warning" /> Flash Deals</span>
                        <h2 className="title-xl fw-bold">Hurry, Ends Soon!</h2>
                    </div>

                    <Swiper
                        slidesPerView={1}
                        spaceBetween={30}
                        autoplay={{ delay: 3000, disableOnInteraction: false }}
                        breakpoints={{
                            640: { slidesPerView: 2 },
                            1024: { slidesPerView: 4 }
                        }}
                        modules={[Autoplay, FreeMode]}
                        className="flash-swiper"
                    >
                        {saleProducts.slice(0, 6).map((product) => (
                            <SwiperSlide key={product.id}>
                                <div className="sale-card-wrapper" onClick={() => navigate(`/product/${product.id}`)}>
                                    <div className="discount-ribbon">-50%</div>
                                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden position-relative" style={{ cursor: 'pointer' }}>
                                        <button 
                                            className={`wishlist_btn ${isInWishlist(product.id) ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleWishlist(product);
                                            }}
                                        >
                                            {isInWishlist(product.id) ? <FaHeart /> : <FaRegHeart />}
                                        </button>
                                        <Card.Img variant="top" src={product.productImage} style={{ height: '300px', objectFit: 'cover' }} />
                                        <Card.Body className="text-center p-4">
                                            <p className="text-muted small mb-1 text-uppercase">{product.category}</p>
                                            <h5 className="fw-bold mb-3">{product.title}</h5>
                                            <div className="price-wrap">
                                                <span className="original-price">${(parseFloat(product.price) * 2).toFixed(2)}</span>
                                                <span className="sale-price">${product.price}</span>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Container>
            </section>

            <Container>
                <div className="promo-banner" data-aos="zoom-in">
                    <h2 className="title-md text-white mb-2">EXTRA 10% OFF YOUR FIRST ORDER</h2>
                    <div className="promo-code-wrap">
                        <code>V2SALE10</code>
                        <Button className="copy-btn" onClick={() => copyToClipboard('V2SALE10')}>
                            <FaCopy /> COPY CODE
                        </Button>
                    </div>
                </div>
            </Container>

            <section className="sales-grid-section">
                <Container>
                    <div className="section-header text-center mb-5" data-aos="fade-up">
                        <span className="section-subtitle"><FaFire className="hot-icon text-danger" /> Trending Now</span>
                        <h2 className="title-xl fw-bold">Best Sellers on Sale</h2>
                    </div>

                    <Row className="g-4">
                        {saleProducts.map((product, idx) => (
                            <Col lg={3} md={4} sm={6} xs={6} key={product.id} data-aos="zoom-in-up" data-aos-delay={idx * 50}>
                                <div className="sale-card-wrapper" onClick={() => navigate(`/product/${product.id}`)}>
                                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100 position-relative" style={{ cursor: 'pointer' }}>
                                        <button 
                                            className={`wishlist_btn ${isInWishlist(product.id) ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleWishlist(product);
                                            }}
                                        >
                                            {isInWishlist(product.id) ? <FaHeart /> : <FaRegHeart />}
                                        </button>
                                        <Card.Img variant="top" src={product.productImage} style={{ height: '280px', objectFit: 'cover' }} />
                                        <Card.Body className="p-4">
                                            <h5 className="fw-bold mb-3 text-truncate-2">{product.title}</h5>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="original-price">${(parseFloat(product.price) * 1.5).toFixed(2)}</span>
                                                <span className="sale-price text-dark">${product.price}</span>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            <section className="bg-light py-5">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} data-aos="fade-right">
                            <h3 className="fw-bold mb-1">Stay Notified</h3>
                            <p className="text-muted mb-0">Get exclusive early access.</p>
                        </Col>
                        <Col lg={6} className="mt-4 mt-lg-0" data-aos="fade-left">
                            <div className="bg-white p-2 rounded-5 shadow-sm d-flex">
                                <input type="email" placeholder="Your email" className="form-control border-0 px-4" />
                                <Button className="dBtn btnPrimary rounded-5 px-3 btn-sm" style={{ fontSize: '12px', height: '36px', display: 'flex', alignItems: 'center' }}>SUBSCRIBE</Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default SalesPage;
